/**
 * Disciplinary Actions Tab - HR & Payroll Page
 * Manage employee disciplinary actions: verbal warning, written warning, suspension, termination
 */
import { useState, useEffect } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import {
  AlertTriangle,
  FileText,
  Plus,
  Edit,
  Trash2,
  User,
  Calendar,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  Eye,
  Printer,
  Download,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ACTION_TYPES = [
  { value: "verbal_warning", label: "Verbal Warning", color: "bg-yellow-100 text-yellow-800", icon: "⚠️" },
  { value: "written_warning", label: "Written Warning", color: "bg-orange-100 text-orange-800", icon: "📝" },
  { value: "suspension", label: "Suspension", color: "bg-red-100 text-red-800", icon: "⏸️" },
  { value: "termination", label: "Termination", color: "bg-red-200 text-red-900", icon: "❌" },
];

const VIOLATION_CATEGORIES = [
  { value: "attendance", label: "Attendance Issues" },
  { value: "conduct", label: "Conduct/Behavior" },
  { value: "performance", label: "Job Performance" },
  { value: "policy_violation", label: "Policy Violation" },
  { value: "safety", label: "Safety Violation" },
  { value: "insubordination", label: "Insubordination" },
  { value: "harassment", label: "Harassment" },
  { value: "theft", label: "Theft/Dishonesty" },
  { value: "other", label: "Other" },
];

const STATUS_COLORS = {
  pending: "bg-yellow-100 text-yellow-800",
  acknowledged: "bg-blue-100 text-blue-800",
  completed: "bg-emerald-100 text-emerald-800",
  appealed: "bg-purple-100 text-purple-800",
};

export function DisciplinaryActionsTab({ employees = [] }) {
  const [actions, setActions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [viewDialogOpen, setViewDialogOpen] = useState(false);
  const [selectedAction, setSelectedAction] = useState(null);
  const [saving, setSaving] = useState(false);
  const [filterEmployee, setFilterEmployee] = useState("");
  const [filterType, setFilterType] = useState("");

  const [formData, setFormData] = useState({
    employee_id: "",
    action_type: "",
    action_date: new Date().toISOString().split("T")[0],
    incident_date: "",
    violation_category: "",
    violation_description: "",
    corrective_action: "",
    improvement_plan: "",
    follow_up_date: "",
    suspension_start_date: "",
    suspension_end_date: "",
    suspension_with_pay: false,
    termination_effective_date: "",
    termination_reason: "",
    eligible_for_rehire: null,
    witnesses: "",
    supervisor_name: "",
  });

  useEffect(() => {
    fetchActions();
  }, [filterEmployee, filterType]);

  const fetchActions = async () => {
    try {
      setLoading(true);
      let url = `${API}/disciplinary-actions`;
      const params = new URLSearchParams();
      if (filterEmployee) params.append("employee_id", filterEmployee);
      if (filterType) params.append("action_type", filterType);
      if (params.toString()) url += `?${params.toString()}`;
      
      const response = await axios.get(url);
      setActions(response.data);
    } catch (error) {
      console.error("Failed to fetch disciplinary actions:", error);
      toast.error("Failed to load disciplinary actions");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      employee_id: "",
      action_type: "",
      action_date: new Date().toISOString().split("T")[0],
      incident_date: "",
      violation_category: "",
      violation_description: "",
      corrective_action: "",
      improvement_plan: "",
      follow_up_date: "",
      suspension_start_date: "",
      suspension_end_date: "",
      suspension_with_pay: false,
      termination_effective_date: "",
      termination_reason: "",
      eligible_for_rehire: null,
      witnesses: "",
      supervisor_name: "",
    });
    setSelectedAction(null);
  };

  const handleSubmit = async () => {
    if (!formData.employee_id || !formData.action_type || !formData.violation_description) {
      toast.error("Please fill in required fields: Employee, Action Type, and Violation Description");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        witnesses: formData.witnesses ? formData.witnesses.split(",").map(w => w.trim()) : [],
      };

      if (selectedAction) {
        await axios.patch(`${API}/disciplinary-actions/${selectedAction.id}`, payload);
        toast.success("Disciplinary action updated");
      } else {
        await axios.post(`${API}/disciplinary-actions`, payload);
        toast.success("Disciplinary action created");
      }
      
      setDialogOpen(false);
      resetForm();
      fetchActions();
    } catch (error) {
      console.error("Failed to save disciplinary action:", error);
      toast.error(error.response?.data?.detail || "Failed to save disciplinary action");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (action) => {
    setSelectedAction(action);
    setFormData({
      employee_id: action.employee_id || "",
      action_type: action.action_type || "",
      action_date: action.action_date || "",
      incident_date: action.incident_date || "",
      violation_category: action.violation_category || "",
      violation_description: action.violation_description || "",
      corrective_action: action.corrective_action || "",
      improvement_plan: action.improvement_plan || "",
      follow_up_date: action.follow_up_date || "",
      suspension_start_date: action.suspension_start_date || "",
      suspension_end_date: action.suspension_end_date || "",
      suspension_with_pay: action.suspension_with_pay || false,
      termination_effective_date: action.termination_effective_date || "",
      termination_reason: action.termination_reason || "",
      eligible_for_rehire: action.eligible_for_rehire,
      witnesses: action.witnesses?.join(", ") || "",
      supervisor_name: action.supervisor_name || "",
    });
    setDialogOpen(true);
  };

  const handleDelete = async (actionId) => {
    try {
      await axios.delete(`${API}/disciplinary-actions/${actionId}`);
      toast.success("Disciplinary action deleted");
      fetchActions();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to delete");
    }
  };

  const handlePrint = (action) => {
    const typeInfo = getActionTypeInfo(action.action_type);
    const violationLabel = getViolationLabel(action.violation_category);
    
    const printWindow = window.open("", "_blank");
    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Disciplinary Action - ${action.employee_name}</title>
          <style>
            body { font-family: Arial, sans-serif; padding: 40px; max-width: 800px; margin: 0 auto; line-height: 1.5; }
            h1 { color: #1e293b; border-bottom: 3px solid #dc2626; padding-bottom: 15px; margin-bottom: 30px; }
            .header-badge { display: inline-block; padding: 6px 16px; border-radius: 20px; font-size: 14px; font-weight: 600; margin-right: 10px; }
            .badge-warning { background: #fef3c7; color: #92400e; }
            .badge-suspension { background: #fee2e2; color: #991b1b; }
            .badge-termination { background: #fecaca; color: #7f1d1d; }
            .section { margin-bottom: 25px; padding: 20px; background: #f8fafc; border-radius: 8px; border-left: 4px solid #dc2626; }
            .section-title { font-size: 12px; font-weight: 600; color: #64748b; text-transform: uppercase; margin-bottom: 8px; }
            .section-content { font-size: 14px; color: #1e293b; }
            .grid { display: grid; grid-template-columns: repeat(2, 1fr); gap: 20px; }
            .field { margin-bottom: 15px; }
            .field-label { font-size: 11px; font-weight: 600; color: #64748b; text-transform: uppercase; }
            .field-value { font-size: 14px; color: #1e293b; margin-top: 4px; }
            .signature-section { margin-top: 40px; padding-top: 30px; border-top: 2px solid #e2e8f0; }
            .signature-grid { display: grid; grid-template-columns: repeat(3, 1fr); gap: 30px; margin-top: 20px; }
            .signature-box { text-align: center; padding-top: 60px; border-top: 1px solid #1e293b; }
            .signature-label { font-size: 12px; color: #64748b; margin-top: 8px; }
            .footer { margin-top: 40px; padding-top: 20px; border-top: 1px solid #e2e8f0; font-size: 11px; color: #94a3b8; text-align: center; }
            @media print { body { padding: 20px; } .no-print { display: none; } }
          </style>
        </head>
        <body>
          <h1>Employee Disciplinary Action Form</h1>
          
          <div style="margin-bottom: 30px;">
            <span class="header-badge ${action.action_type === 'termination' ? 'badge-termination' : action.action_type === 'suspension' ? 'badge-suspension' : 'badge-warning'}">
              ${typeInfo.icon} ${typeInfo.label}
            </span>
            <span class="header-badge" style="background: #e2e8f0; color: #475569;">
              ${action.status?.charAt(0).toUpperCase() + action.status?.slice(1) || 'Pending'}
            </span>
          </div>

          <div class="grid">
            <div class="field">
              <div class="field-label">Employee Name</div>
              <div class="field-value">${action.employee_name || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Action Date</div>
              <div class="field-value">${action.action_date || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Incident Date</div>
              <div class="field-value">${action.incident_date || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Violation Category</div>
              <div class="field-value">${violationLabel || 'N/A'}</div>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Violation Description</div>
            <div class="section-content">${action.violation_description || 'N/A'}</div>
          </div>

          ${action.corrective_action ? `
          <div class="section">
            <div class="section-title">Corrective Action Required</div>
            <div class="section-content">${action.corrective_action}</div>
          </div>
          ` : ''}

          ${action.improvement_plan ? `
          <div class="section">
            <div class="section-title">Improvement Plan</div>
            <div class="section-content">${action.improvement_plan}</div>
          </div>
          ` : ''}

          ${action.follow_up_date ? `
          <div class="field">
            <div class="field-label">Follow-up Date</div>
            <div class="field-value">${action.follow_up_date}</div>
          </div>
          ` : ''}

          ${action.action_type === 'suspension' ? `
          <div class="section" style="border-left-color: #f59e0b;">
            <div class="section-title">Suspension Details</div>
            <div class="grid">
              <div class="field">
                <div class="field-label">Start Date</div>
                <div class="field-value">${action.suspension_start_date || 'N/A'}</div>
              </div>
              <div class="field">
                <div class="field-label">End Date</div>
                <div class="field-value">${action.suspension_end_date || 'N/A'}</div>
              </div>
            </div>
            <div class="field">
              <div class="field-label">With Pay</div>
              <div class="field-value">${action.suspension_with_pay ? 'Yes' : 'No'}</div>
            </div>
          </div>
          ` : ''}

          ${action.action_type === 'termination' ? `
          <div class="section" style="border-left-color: #7f1d1d;">
            <div class="section-title">Termination Details</div>
            <div class="field">
              <div class="field-label">Effective Date</div>
              <div class="field-value">${action.termination_effective_date || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Reason</div>
              <div class="field-value">${action.termination_reason || 'N/A'}</div>
            </div>
            <div class="field">
              <div class="field-label">Eligible for Rehire</div>
              <div class="field-value">${action.eligible_for_rehire === true ? 'Yes' : action.eligible_for_rehire === false ? 'No' : 'N/A'}</div>
            </div>
          </div>
          ` : ''}

          ${action.witnesses?.length > 0 ? `
          <div class="field">
            <div class="field-label">Witnesses</div>
            <div class="field-value">${action.witnesses.join(', ')}</div>
          </div>
          ` : ''}

          <div class="signature-section">
            <div class="section-title">Acknowledgment & Signatures</div>
            <p style="font-size: 12px; color: #64748b; margin-bottom: 20px;">
              By signing below, the employee acknowledges receipt of this disciplinary action. 
              Signature does not necessarily indicate agreement with the action taken.
            </p>
            <div class="signature-grid">
              <div>
                <div class="signature-box"></div>
                <div class="signature-label">Employee Signature</div>
                <div style="font-size: 10px; color: #94a3b8;">Date: _____________</div>
              </div>
              <div>
                <div class="signature-box"></div>
                <div class="signature-label">Supervisor Signature</div>
                <div style="font-size: 10px; color: #94a3b8;">Date: _____________</div>
              </div>
              <div>
                <div class="signature-box"></div>
                <div class="signature-label">HR Representative</div>
                <div style="font-size: 10px; color: #94a3b8;">Date: _____________</div>
              </div>
            </div>
          </div>

          <div class="footer">
            <p>Generated on ${new Date().toLocaleString()}</p>
            <p>This document is confidential and part of the employee's personnel file.</p>
          </div>
        </body>
      </html>
    `);
    printWindow.document.close();
    printWindow.print();
  };

  const handleDownload = (action) => {
    const typeInfo = getActionTypeInfo(action.action_type);
    const violationLabel = getViolationLabel(action.violation_category);
    
    const content = `
EMPLOYEE DISCIPLINARY ACTION FORM
==================================

Action Type: ${typeInfo.label}
Status: ${action.status?.charAt(0).toUpperCase() + action.status?.slice(1) || 'Pending'}

EMPLOYEE INFORMATION
--------------------
Employee Name: ${action.employee_name || 'N/A'}
Action Date: ${action.action_date || 'N/A'}
Incident Date: ${action.incident_date || 'N/A'}
Violation Category: ${violationLabel || 'N/A'}

VIOLATION DESCRIPTION
---------------------
${action.violation_description || 'N/A'}

CORRECTIVE ACTION REQUIRED
--------------------------
${action.corrective_action || 'N/A'}

IMPROVEMENT PLAN
----------------
${action.improvement_plan || 'N/A'}

Follow-up Date: ${action.follow_up_date || 'N/A'}

${action.action_type === 'suspension' ? `
SUSPENSION DETAILS
------------------
Start Date: ${action.suspension_start_date || 'N/A'}
End Date: ${action.suspension_end_date || 'N/A'}
With Pay: ${action.suspension_with_pay ? 'Yes' : 'No'}
` : ''}

${action.action_type === 'termination' ? `
TERMINATION DETAILS
-------------------
Effective Date: ${action.termination_effective_date || 'N/A'}
Reason: ${action.termination_reason || 'N/A'}
Eligible for Rehire: ${action.eligible_for_rehire === true ? 'Yes' : action.eligible_for_rehire === false ? 'No' : 'N/A'}
` : ''}

${action.witnesses?.length > 0 ? `Witnesses: ${action.witnesses.join(', ')}` : ''}
Supervisor: ${action.supervisor_name || 'N/A'}

SIGNATURES
----------
Employee Signature: _________________________ Date: _________
${action.employee_refused_to_sign ? '** Employee refused to sign **' : ''}

Supervisor Signature: _________________________ Date: _________

HR Representative: _________________________ Date: _________

==================================
Generated: ${new Date().toLocaleString()}
This document is confidential and part of the employee's personnel file.
    `.trim();

    const blob = new Blob([content], { type: "text/plain" });
    const url = URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `disciplinary_action_${action.employee_name?.replace(/\s+/g, '_') || 'unknown'}_${action.action_date || 'undated'}.txt`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
    toast.success("Disciplinary action downloaded");
  };

  const getActionTypeInfo = (type) => ACTION_TYPES.find(t => t.value === type) || { label: type, color: "bg-slate-100 text-slate-800", icon: "📋" };
  const getViolationLabel = (cat) => VIOLATION_CATEGORIES.find(c => c.value === cat)?.label || cat;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h3 className="text-lg font-semibold text-slate-800">Disciplinary Actions</h3>
          <p className="text-sm text-slate-500">Manage employee disciplinary records</p>
        </div>
        <Button
          onClick={() => { resetForm(); setDialogOpen(true); }}
          className="bg-red-600 hover:bg-red-700"
          data-testid="add-disciplinary-btn"
        >
          <Plus className="w-4 h-4 mr-2" />
          New Disciplinary Action
        </Button>
      </div>

      {/* Filters */}
      <div className="flex gap-4 p-4 bg-slate-50 rounded-lg">
        <div className="flex-1">
          <Label className="text-xs text-slate-500">Filter by Employee</Label>
          <Select value={filterEmployee || "all"} onValueChange={(v) => setFilterEmployee(v === "all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Employees" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Employees</SelectItem>
              {employees.map(emp => (
                <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="flex-1">
          <Label className="text-xs text-slate-500">Filter by Type</Label>
          <Select value={filterType || "all"} onValueChange={(v) => setFilterType(v === "all" ? "" : v)}>
            <SelectTrigger>
              <SelectValue placeholder="All Types" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {ACTION_TYPES.map(type => (
                <SelectItem key={type.value} value={type.value}>{type.icon} {type.label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Actions List */}
      {loading ? (
        <div className="text-center py-8 text-slate-500">Loading...</div>
      ) : actions.length === 0 ? (
        <div className="text-center py-12 bg-slate-50 rounded-lg">
          <AlertTriangle className="w-12 h-12 mx-auto text-slate-300 mb-4" />
          <p className="text-slate-500">No disciplinary actions found</p>
        </div>
      ) : (
        <div className="space-y-3">
          {actions.map((action) => {
            const typeInfo = getActionTypeInfo(action.action_type);
            return (
              <div
                key={action.id}
                className="p-4 border rounded-lg hover:shadow-md transition-shadow bg-white"
                data-testid={`disciplinary-action-${action.id}`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${typeInfo.color}`}>
                        {typeInfo.icon} {typeInfo.label}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[action.status] || "bg-slate-100 text-slate-700"}`}>
                        {action.status?.charAt(0).toUpperCase() + action.status?.slice(1)}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-slate-900 font-medium">
                      <User className="w-4 h-4 text-slate-400" />
                      {action.employee_name || "Unknown Employee"}
                    </div>
                    <div className="flex items-center gap-4 mt-2 text-sm text-slate-500">
                      <span className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        {action.action_date}
                      </span>
                      {action.violation_category && (
                        <span className="flex items-center gap-1">
                          <AlertCircle className="w-3 h-3" />
                          {getViolationLabel(action.violation_category)}
                        </span>
                      )}
                    </div>
                    <p className="mt-2 text-sm text-slate-600 line-clamp-2">
                      {action.violation_description}
                    </p>
                  </div>
                  <div className="flex gap-2">
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => { setSelectedAction(action); setViewDialogOpen(true); }}
                      title="View Details"
                    >
                      <Eye className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleEdit(action)}
                      title="Edit"
                    >
                      <Edit className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handlePrint(action)}
                      title="Print"
                    >
                      <Printer className="w-4 h-4" />
                    </Button>
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => handleDownload(action)}
                      title="Download"
                    >
                      <Download className="w-4 h-4" />
                    </Button>
                    <AlertDialog>
                      <AlertDialogTrigger asChild>
                        <Button size="sm" variant="outline" className="text-red-600">
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </AlertDialogTrigger>
                      <AlertDialogContent>
                        <AlertDialogHeader>
                          <AlertDialogTitle>Delete Disciplinary Action?</AlertDialogTitle>
                          <AlertDialogDescription>
                            This will permanently delete this disciplinary record. This action cannot be undone.
                          </AlertDialogDescription>
                        </AlertDialogHeader>
                        <AlertDialogFooter>
                          <AlertDialogCancel>Cancel</AlertDialogCancel>
                          <AlertDialogAction
                            onClick={() => handleDelete(action.id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </AlertDialogAction>
                        </AlertDialogFooter>
                      </AlertDialogContent>
                    </AlertDialog>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 text-red-600" />
              {selectedAction ? "Edit Disciplinary Action" : "New Disciplinary Action"}
            </DialogTitle>
          </DialogHeader>

          <div className="space-y-4">
            {/* Employee & Type */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Employee *</Label>
                <Select
                  value={formData.employee_id}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, employee_id: value }))}
                >
                  <SelectTrigger data-testid="employee-select">
                    <SelectValue placeholder="Select employee" />
                  </SelectTrigger>
                  <SelectContent>
                    {employees.map(emp => (
                      <SelectItem key={emp.id} value={emp.id}>{emp.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Action Type *</Label>
                <Select
                  value={formData.action_type}
                  onValueChange={(value) => setFormData(prev => ({ ...prev, action_type: value }))}
                >
                  <SelectTrigger data-testid="action-type-select">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {ACTION_TYPES.map(type => (
                      <SelectItem key={type.value} value={type.value}>
                        {type.icon} {type.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Dates */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Action Date *</Label>
                <Input
                  type="date"
                  value={formData.action_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, action_date: e.target.value }))}
                />
              </div>
              <div className="space-y-2">
                <Label>Incident Date</Label>
                <Input
                  type="date"
                  value={formData.incident_date}
                  onChange={(e) => setFormData(prev => ({ ...prev, incident_date: e.target.value }))}
                />
              </div>
            </div>

            {/* Violation */}
            <div className="space-y-2">
              <Label>Violation Category</Label>
              <Select
                value={formData.violation_category}
                onValueChange={(value) => setFormData(prev => ({ ...prev, violation_category: value }))}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {VIOLATION_CATEGORIES.map(cat => (
                    <SelectItem key={cat.value} value={cat.value}>{cat.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="space-y-2">
              <Label>Violation Description *</Label>
              <Textarea
                value={formData.violation_description}
                onChange={(e) => setFormData(prev => ({ ...prev, violation_description: e.target.value }))}
                placeholder="Describe the violation in detail..."
                rows={3}
              />
            </div>

            {/* Corrective Action */}
            <div className="space-y-2">
              <Label>Corrective Action Required</Label>
              <Textarea
                value={formData.corrective_action}
                onChange={(e) => setFormData(prev => ({ ...prev, corrective_action: e.target.value }))}
                placeholder="What corrective action is required..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Improvement Plan</Label>
              <Textarea
                value={formData.improvement_plan}
                onChange={(e) => setFormData(prev => ({ ...prev, improvement_plan: e.target.value }))}
                placeholder="Steps for improvement..."
                rows={2}
              />
            </div>

            <div className="space-y-2">
              <Label>Follow-up Date</Label>
              <Input
                type="date"
                value={formData.follow_up_date}
                onChange={(e) => setFormData(prev => ({ ...prev, follow_up_date: e.target.value }))}
              />
            </div>

            {/* Suspension Fields */}
            {formData.action_type === "suspension" && (
              <div className="p-4 bg-red-50 rounded-lg space-y-4">
                <h4 className="font-medium text-red-800">Suspension Details</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Suspension Start Date</Label>
                    <Input
                      type="date"
                      value={formData.suspension_start_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, suspension_start_date: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Suspension End Date</Label>
                    <Input
                      type="date"
                      value={formData.suspension_end_date}
                      onChange={(e) => setFormData(prev => ({ ...prev, suspension_end_date: e.target.value }))}
                    />
                  </div>
                </div>
                <label className="flex items-center gap-2">
                  <input
                    type="checkbox"
                    checked={formData.suspension_with_pay}
                    onChange={(e) => setFormData(prev => ({ ...prev, suspension_with_pay: e.target.checked }))}
                    className="rounded"
                  />
                  <span className="text-sm">Suspension with pay</span>
                </label>
              </div>
            )}

            {/* Termination Fields */}
            {formData.action_type === "termination" && (
              <div className="p-4 bg-red-100 rounded-lg space-y-4">
                <h4 className="font-medium text-red-900">Termination Details</h4>
                <div className="space-y-2">
                  <Label>Effective Date</Label>
                  <Input
                    type="date"
                    value={formData.termination_effective_date}
                    onChange={(e) => setFormData(prev => ({ ...prev, termination_effective_date: e.target.value }))}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Termination Reason</Label>
                  <Textarea
                    value={formData.termination_reason}
                    onChange={(e) => setFormData(prev => ({ ...prev, termination_reason: e.target.value }))}
                    placeholder="Reason for termination..."
                    rows={2}
                  />
                </div>
                <div className="space-y-2">
                  <Label>Eligible for Rehire?</Label>
                  <Select
                    value={formData.eligible_for_rehire === null ? "" : formData.eligible_for_rehire ? "yes" : "no"}
                    onValueChange={(value) => setFormData(prev => ({ ...prev, eligible_for_rehire: value === "yes" }))}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="yes">Yes</SelectItem>
                      <SelectItem value="no">No</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Witnesses & Supervisor */}
            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Witnesses (comma-separated)</Label>
                <Input
                  value={formData.witnesses}
                  onChange={(e) => setFormData(prev => ({ ...prev, witnesses: e.target.value }))}
                  placeholder="John Doe, Jane Smith"
                />
              </div>
              <div className="space-y-2">
                <Label>Supervisor Name</Label>
                <Input
                  value={formData.supervisor_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, supervisor_name: e.target.value }))}
                  placeholder="Supervisor name"
                />
              </div>
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleSubmit}
              disabled={saving}
              className="bg-red-600 hover:bg-red-700"
            >
              {saving ? "Saving..." : selectedAction ? "Update" : "Create Disciplinary Action"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* View Dialog */}
      <Dialog open={viewDialogOpen} onOpenChange={setViewDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Disciplinary Action Details</DialogTitle>
          </DialogHeader>
          {selectedAction && (
            <div className="space-y-4">
              <div className="flex items-center gap-3">
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${getActionTypeInfo(selectedAction.action_type).color}`}>
                  {getActionTypeInfo(selectedAction.action_type).icon} {getActionTypeInfo(selectedAction.action_type).label}
                </span>
                <span className={`px-3 py-1 rounded-full text-sm font-medium ${STATUS_COLORS[selectedAction.status]}`}>
                  {selectedAction.status?.charAt(0).toUpperCase() + selectedAction.status?.slice(1)}
                </span>
              </div>

              <div className="grid grid-cols-2 gap-4 p-4 bg-slate-50 rounded-lg">
                <div>
                  <p className="text-xs text-slate-500">Employee</p>
                  <p className="font-medium">{selectedAction.employee_name}</p>
                </div>
                <div>
                  <p className="text-xs text-slate-500">Action Date</p>
                  <p className="font-medium">{selectedAction.action_date}</p>
                </div>
                {selectedAction.incident_date && (
                  <div>
                    <p className="text-xs text-slate-500">Incident Date</p>
                    <p className="font-medium">{selectedAction.incident_date}</p>
                  </div>
                )}
                {selectedAction.violation_category && (
                  <div>
                    <p className="text-xs text-slate-500">Violation Category</p>
                    <p className="font-medium">{getViolationLabel(selectedAction.violation_category)}</p>
                  </div>
                )}
              </div>

              <div>
                <p className="text-xs text-slate-500 mb-1">Violation Description</p>
                <p className="p-3 bg-slate-50 rounded-lg">{selectedAction.violation_description}</p>
              </div>

              {selectedAction.corrective_action && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Corrective Action Required</p>
                  <p className="p-3 bg-slate-50 rounded-lg">{selectedAction.corrective_action}</p>
                </div>
              )}

              {selectedAction.improvement_plan && (
                <div>
                  <p className="text-xs text-slate-500 mb-1">Improvement Plan</p>
                  <p className="p-3 bg-slate-50 rounded-lg">{selectedAction.improvement_plan}</p>
                </div>
              )}

              {/* Signatures */}
              <div className="p-4 bg-slate-100 rounded-lg">
                <h4 className="font-medium mb-3">Signatures</h4>
                <div className="grid grid-cols-3 gap-4 text-sm">
                  <div>
                    <p className="text-slate-500">Employee</p>
                    {selectedAction.employee_refused_to_sign ? (
                      <p className="text-red-600">Refused to sign</p>
                    ) : selectedAction.employee_signature ? (
                      <p className="text-emerald-600">✓ Signed {selectedAction.employee_signature_date}</p>
                    ) : (
                      <p className="text-amber-600">Pending</p>
                    )}
                  </div>
                  <div>
                    <p className="text-slate-500">Supervisor</p>
                    {selectedAction.supervisor_signature ? (
                      <p className="text-emerald-600">✓ {selectedAction.supervisor_name}</p>
                    ) : (
                      <p className="text-amber-600">Pending</p>
                    )}
                  </div>
                  <div>
                    <p className="text-slate-500">HR</p>
                    {selectedAction.hr_signature ? (
                      <p className="text-emerald-600">✓ {selectedAction.hr_name}</p>
                    ) : (
                      <p className="text-amber-600">Pending</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
          <DialogFooter className="flex gap-2">
            <Button variant="outline" onClick={() => handlePrint(selectedAction)} className="gap-2">
              <Printer className="w-4 h-4" />
              Print
            </Button>
            <Button variant="outline" onClick={() => handleDownload(selectedAction)} className="gap-2">
              <Download className="w-4 h-4" />
              Download
            </Button>
            <Button variant="outline" onClick={() => { handleEdit(selectedAction); setViewDialogOpen(false); }} className="gap-2">
              <Edit className="w-4 h-4" />
              Edit
            </Button>
            <Button onClick={() => setViewDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
